import { useField } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useMemo } from 'react';

import { Text } from '@components/Text';
import { columnDirection } from '@enums';
import type { DualListBoxProps } from '@types';
import { cn, validateValueEqual } from '@utils';

import ErrorMessage from '../TextInput/errorMessage';
import { SelectableItemList } from './SelectableItemList';

export const DualListBox = ({
  name,
  options,
  leftPanel,
  rightPanel,
  direction = columnDirection.Column,
  showValue = true,
  label = '',
  required = false,
  titleFontSize = 4,
  labelClassName = '',
  errorClassName = '',
}: DualListBoxProps) => {
  const [field, meta, helpers] = useField<string[]>(name);

  const isError = meta.error && meta.touched;

  const isDirectionRow = validateValueEqual(direction, columnDirection.Row);

  const leftPanelList = useMemo(
    () => options.filter((item) => !field.value.includes(item.value)),
    [options, field.value],
  );
  const rightPanelList = useMemo(
    () => options.filter((item) => field.value.includes(item.value)),
    [options, field.value],
  );

  const handleMoveAllToRightPanel = useCallback(() => {
    helpers.setValue([...field.value, ...leftPanelList.map((c) => c.value)]);
  }, [field.value]);

  const handleMoveAllToLeftPanel = useCallback(() => {
    helpers.setValue([]);
  }, []);

  const handleToggle = useCallback(
    (item: string, addToBlocked: boolean) => {
      if (addToBlocked) {
        helpers.setValue([...field.value, item]);
      } else {
        helpers.setValue(field.value.filter((c) => c !== item));
      }
    },
    [field.value],
  );

  return (
    <>
      {Boolean(label) && (
        <Text $level={5} $customizeColor className="text-us flex items-center text-center w-full text-gray-700">
          {`${label} ${required ? '*' : ''}`}
        </Text>
      )}

      <div className={cn('flex gap-6 flex-col', isDirectionRow ? 'sm:flex-row' : 'sm:flex-col')}>
        <SelectableItemList
          items={leftPanelList}
          onToggleItem={(item) => handleToggle(item, true)}
          onMoveAll={handleMoveAllToRightPanel}
          placeholder={leftPanel.placeholder}
          title={leftPanel.title}
          name={leftPanel.fieldName}
          showValue={showValue}
          titleFontSize={titleFontSize}
          labelClassName={labelClassName}
          zone={0}
        />
        <SelectableItemList
          items={rightPanelList}
          onToggleItem={(item) => handleToggle(item, false)}
          onMoveAll={handleMoveAllToLeftPanel}
          placeholder={rightPanel.placeholder}
          title={rightPanel.title}
          name={rightPanel.fieldName}
          showValue={showValue}
          titleFontSize={titleFontSize}
          labelClassName={labelClassName}
          zone={1}
        />
      </div>

      <AnimatePresence>
        {isError && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <ErrorMessage isError={isError} errorClassName={errorClassName} meta={meta} />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
